import http from "./http";
import {
	DEFAULT_TEXT_PALETTE,
	normalizeStationsTextRegistry,
	normalizeDetection,
	type StationsTextRegistry,
	type StationsTextRegistryAdminPayload,
	type StationsTextDetection,
} from "./StationsTextConfigService";
import type { GlossaryTerm } from "../views/pages/TextWorkspace/types";

const ROUTES = {
	stations: {
		registry: "/utility/stations-text-registry",
		draft: "/utility/stations-text-registry/draft",
		publish: "/utility/stations-text-registry/publish",
		detections: "/utility/stations-text-detections",
		ignore: "/utility/stations-text-detections/ignore",
		glossary: "/utility/stations-text-glossary",
	},
	homepage: {
		registry: "/utility/homepage-text-registry",
		draft: "/utility/homepage-text-registry/draft",
		publish: "/utility/homepage-text-registry/publish",
		detections: "/utility/homepage-text-detections",
		ignore: "/utility/homepage-text-detections/ignore",
		glossary: "/utility/homepage-text-glossary",
	},
	admin: {
		registry: "/utility/admin-text-registry",
		draft: "/utility/admin-text-registry/draft",
		publish: "/utility/admin-text-registry/publish",
		detections: "/utility/admin-text-detections",
		ignore: "/utility/admin-text-detections/ignore",
		glossary: "/utility/admin-text-glossary",
	},
} as const;

export type TextRegistryScope = keyof typeof ROUTES;

export function createTextRegistryAdminApi(scope: TextRegistryScope) {
	const r = ROUTES[scope];

	return {
		async getRegistryAdmin(): Promise<StationsTextRegistryAdminPayload> {
			const { data } = await http.get(r.registry);
			const payload = data?.data || {};

			return {
				draft: normalizeStationsTextRegistry(payload?.draft, "draft"),
				published: normalizeStationsTextRegistry(payload?.published, "published"),
				palette:
					Array.isArray(payload?.palette) && payload.palette.length > 0
						? payload.palette
						: DEFAULT_TEXT_PALETTE,
			};
		},

		async saveDraft(registry: StationsTextRegistry) {
			const { data } = await http.post(r.draft, { data: registry });
			return normalizeStationsTextRegistry(data?.data, "draft");
		},

		async publish() {
			const { data } = await http.post(r.publish);
			return normalizeStationsTextRegistry(data?.data, "published");
		},

		async getDetections(): Promise<StationsTextDetection[]> {
			const { data } = await http.get(r.detections);
			return Array.isArray(data?.data)
				? data.data.map((item: StationsTextDetection) => normalizeDetection(item)).filter(Boolean)
				: [];
		},

		async ignoreDetection(fingerprint: string, note = "") {
			const { data } = await http.post(r.ignore, {
				data: { fingerprint, note },
			});
			return Array.isArray(data?.data)
				? data.data.map((item: StationsTextDetection) => normalizeDetection(item)).filter(Boolean)
				: [];
		},

		async listGlossary(): Promise<GlossaryTerm[]> {
			const { data } = await http.get(r.glossary);
			return Array.isArray(data?.data) ? data.data : [];
		},

		async upsertGlossary(payload: Omit<GlossaryTerm, "id" | "scope">): Promise<GlossaryTerm> {
			const { data } = await http.post(r.glossary, payload);
			return data?.data as GlossaryTerm;
		},

		async deleteGlossary(id: number): Promise<void> {
			await http.delete(`${r.glossary}/${id}`);
		},
	};
}
